/**
 * Nakama Configuration
 * Configuration for different Nakama server environments
 * Based on the old Lua/Defold project structure
 */

interface NakamaEnvironmentConfig {
  host: string;
  port: number;
  useSSL: boolean;
  serverKey: string;
  httpKey: string;
}

type Environment = 'local' | 'develop' | 'staging' | 'production';

/**
 * Nakama server configurations for all environments
 * Matches the configuration from the old project's nakama-config.lua
 */
const NAKAMA_CONFIG: Record<Environment, NakamaEnvironmentConfig> = {
  local: {
    host: '127.0.0.1',
    port: 7350,
    useSSL: false,
    serverKey: 'ponder-nakama-socket-server-key',
    httpKey: 'defaulthttpkey',
  },
  develop: {
    host: 'server.ponder-flight-game-develop.playponder.com',
    port: 443,
    useSSL: true,
    serverKey: 'ponder-nakama-socket-server-key',
    httpKey: import.meta.env.VITE_PONDER_FLIGHT_GAME_NAKAMA_HTTP_KEY,
  },
  staging: {
    host: 'server.ponder-flight-game-staging.playponder.com',
    port: 443,
    useSSL: true,
    serverKey: 'ponder-nakama-socket-server-key',
    httpKey: import.meta.env.VITE_PONDER_FLIGHT_GAME_NAKAMA_HTTP_KEY,
  },
  production: {
    host: 'server.ponder-flight-game.playponder.com',
    port: 443,
    useSSL: true,
    serverKey: 'ponder-nakama-socket-server-key',
    httpKey: import.meta.env.VITE_PONDER_FLIGHT_GAME_NAKAMA_HTTP_KEY,
  },
};

/**
 * Gets the current environment based on Vite's mode and version
 * Similar to the old project's detect_environment function
 * Priority:
 * 1. Environment variable (VITE_ENVIRONMENT)
 * 2. Dev mode detection → 'local'
 * 3. Default to 'production' for builds
 * 
 * Note: Version-based detection happens at build time via CI/CD
 */
export const getCurrentEnvironment = (): Environment => {
  // Check explicit environment variable
  const envVar = import.meta.env.VITE_ENVIRONMENT as Environment;
  
  console.log('[NakamaConfig] Environment detection:', {
    VITE_ENVIRONMENT: envVar,
    'import.meta.env.DEV': import.meta.env.DEV,
    'import.meta.env.MODE': import.meta.env.MODE,
  });
  
  if (envVar && NAKAMA_CONFIG[envVar]) {
    console.log(`[NakamaConfig] Using VITE_ENVIRONMENT: ${envVar}`);
    return envVar;
  }

  // In development mode, use local
  if (import.meta.env.DEV) {
    console.log('[NakamaConfig] Using DEV mode: local');
    return 'local';
  }

  // Default to production for builds
  // The actual environment will be set via VITE_ENVIRONMENT during deployment
  console.log('[NakamaConfig] Using default: production');
  return 'production';
};

/**
 * Gets the configuration for the current environment
 */
export const getCurrentConfig = (): NakamaEnvironmentConfig => {
  const env = getCurrentEnvironment();
  console.log(`[NakamaConfig] Using environment: ${env}`);
  return NAKAMA_CONFIG[env];
};
